/**
 * Theme Composable - Shared
 * Provides dynamic theming capabilities from restaurant configuration
 */
import { computed, inject, watchEffect } from 'vue';
import { PROJECT_CONFIG_KEY } from './useProjectConfig';
import { getRuntimeSiteTheme } from './useSiteConfig';
/**
 * Default theme colors (fallback)
 */
const defaultTheme = {
    colors: {
        primary: '#2563eb', // blue-600
        primaryHover: '#1d4ed8', // blue-700
        secondary: '#64748b', // slate-500
        secondaryHover: '#475569', // slate-600
        accent: '#0ea5e9', // sky-500
        accentHover: '#0284c7', // sky-600
        // Semantic colors
        success: '#16a34a', // green-600
        successHover: '#15803d', // green-700
        danger: '#dc2626', // red-600
        dangerHover: '#b91c1c', // red-700
        warning: '#d97706', // amber-600
        warningHover: '#b45309', // amber-700
        info: '#0284c7', // sky-600
        infoHover: '#0369a1', // sky-700
        // Background colors
        background: '#ffffff',
        surface: '#f8fafc', // slate-50
        surfaceHover: '#f1f5f9', // slate-100
        // Text colors
        textPrimary: '#0f172a', // slate-900
        textSecondary: '#475569', // slate-600
        textMuted: '#94a3b8' // slate-400
    },
    borderRadius: '0.5rem',
    spacing: {
        small: '0.5rem',
        medium: '1rem',
        large: '1.5rem'
    }
};
/**
 * Theme Composable
 * Automatically injects theme from restaurant config
 */
export function useTheme() {
    // Prefer strongly typed project config injection key used by project apps
    const projectConfigByKey = inject(PROJECT_CONFIG_KEY, null);
    // Fallback for apps using RestaurantPlugin string key
    const projectConfigByString = inject('projectConfig', null);
    const projectContext = inject('projectContext', null);
    const buildThemeFromBranding = (branding) => {
        if (!branding?.colors)
            return null;
        const primary = branding.colors.primary || defaultTheme.colors.primary;
        const secondary = branding.colors.secondary || defaultTheme.colors.secondary;
        const accent = branding.colors.accent || defaultTheme.colors.accent;
        const success = branding.colors.success || defaultTheme.colors.success;
        // ✅ Accept both `danger` and `error` as project branding keys so projects
        // that only define `error` (common in design tokens) still get a proper red.
        const danger = branding.colors.danger ||
            branding.colors.error ||
            defaultTheme.colors.danger;
        const warning = branding.colors.warning || defaultTheme.colors.warning;
        const info = branding.colors.info || defaultTheme.colors.info;
        return {
            colors: {
                primary,
                primaryHover: branding.colors.primaryHover || adjustColorBrightness(primary, -20),
                secondary,
                secondaryHover: branding.colors.secondaryHover || adjustColorBrightness(secondary, -20),
                accent,
                accentHover: branding.colors.accentHover || adjustColorBrightness(accent, -20),
                success,
                successHover: branding.colors.successHover || adjustColorBrightness(success, -20),
                danger,
                dangerHover: branding.colors.dangerHover || adjustColorBrightness(danger, -20),
                warning,
                warningHover: branding.colors.warningHover || adjustColorBrightness(warning, -20),
                info,
                infoHover: branding.colors.infoHover || adjustColorBrightness(info, -20),
                background: branding.colors.background || defaultTheme.colors.background,
                surface: branding.colors.surface || defaultTheme.colors.surface,
                surfaceHover: branding.colors.surfaceHover || defaultTheme.colors.surfaceHover,
                textPrimary: branding.colors.textPrimary || defaultTheme.colors.textPrimary,
                textSecondary: branding.colors.textSecondary || defaultTheme.colors.textSecondary,
                textMuted: branding.colors.textMuted || defaultTheme.colors.textMuted
            },
            borderRadius: branding.borderRadius || defaultTheme.borderRadius,
            fontFamily: branding.fontFamily,
            spacing: branding.spacing || defaultTheme.spacing,
            cart: branding.cart
        };
    };
    const buildThemeFromLegacyTheme = (legacyTheme) => {
        if (!legacyTheme)
            return null;
        const primary = legacyTheme.primaryColor || defaultTheme.colors.primary;
        const secondary = legacyTheme.secondaryColor || defaultTheme.colors.secondary;
        const accent = legacyTheme.accentColor || defaultTheme.colors.accent;
        return {
            colors: {
                ...defaultTheme.colors,
                primary,
                primaryHover: legacyTheme.primaryColorHover || adjustColorBrightness(primary, -20),
                secondary,
                secondaryHover: legacyTheme.secondaryColorHover || adjustColorBrightness(secondary, -20),
                accent,
                accentHover: legacyTheme.accentColorHover || adjustColorBrightness(accent, -20)
            },
            borderRadius: legacyTheme.borderRadius || defaultTheme.borderRadius,
            fontFamily: legacyTheme.fontFamily,
            spacing: defaultTheme.spacing
        };
    };
    // Overlay: vom Server geladene Runtime-Site-Config (Portal-Overrides)
    // ueberschreibt NUR explizit gesetzte Werte des Build-Themes.
    const applyRuntimeOverrides = (base) => {
        const runtime = getRuntimeSiteTheme();
        if (!runtime)
            return base;
        const colors = { ...base.colors };
        if (runtime.primaryColor) {
            colors.primary = runtime.primaryColor;
            colors.primaryHover = adjustColorBrightness(runtime.primaryColor, -20);
        }
        if (runtime.secondaryColor) {
            colors.secondary = runtime.secondaryColor;
            colors.secondaryHover = adjustColorBrightness(runtime.secondaryColor, -20);
        }
        if (runtime.accentColor) {
            colors.accent = runtime.accentColor;
            colors.accentHover = adjustColorBrightness(runtime.accentColor, -20);
        }
        if (runtime.backgroundColor)
            colors.background = runtime.backgroundColor;
        if (runtime.textColor)
            colors.textPrimary = runtime.textColor;
        return {
            ...base,
            colors,
            borderRadius: runtime.borderRadius || base.borderRadius,
            fontFamily: runtime.font || base.fontFamily
        };
    };
    // Extract theme from config or use defaults
    const theme = computed(() => {
        const globalProjectConfig = typeof window !== 'undefined' ? window.projectConfig : null;
        const candidates = [
            projectConfigByKey,
            projectConfigByString,
            projectContext,
            globalProjectConfig
        ];
        for (const candidate of candidates) {
            if (!candidate)
                continue;
            const brandingTheme = buildThemeFromBranding(candidate.branding);
            if (brandingTheme)
                return applyRuntimeOverrides(brandingTheme);
            const legacyTheme = buildThemeFromLegacyTheme(candidate.theme || candidate.app?.theme);
            if (legacyTheme)
                return applyRuntimeOverrides(legacyTheme);
        }
        return applyRuntimeOverrides(defaultTheme);
    });
    /**
     * Get CSS custom properties for theme
     */
    const themeCSSVariables = computed(() => ({
        // Primary colors
        '--theme-primary': theme.value.colors.primary,
        '--theme-primary-hover': theme.value.colors.primaryHover,
        // Secondary colors
        '--theme-secondary': theme.value.colors.secondary,
        '--theme-secondary-hover': theme.value.colors.secondaryHover,
        // Accent colors
        '--theme-accent': theme.value.colors.accent,
        '--theme-accent-hover': theme.value.colors.accentHover,
        // Semantic colors
        '--theme-success': theme.value.colors.success,
        '--theme-success-hover': theme.value.colors.successHover,
        '--theme-danger': theme.value.colors.danger,
        '--theme-danger-hover': theme.value.colors.dangerHover,
        '--theme-warning': theme.value.colors.warning,
        '--theme-warning-hover': theme.value.colors.warningHover,
        '--theme-info': theme.value.colors.info,
        '--theme-info-hover': theme.value.colors.infoHover,
        // Background colors
        '--theme-background': theme.value.colors.background,
        '--theme-surface': theme.value.colors.surface,
        '--theme-surface-hover': theme.value.colors.surfaceHover,
        // Text colors
        '--theme-text-primary': theme.value.colors.textPrimary,
        '--theme-text-secondary': theme.value.colors.textSecondary,
        '--theme-text-muted': theme.value.colors.textMuted,
        // Layout
        '--theme-border-radius': theme.value.borderRadius,
        '--theme-spacing-small': theme.value.spacing?.small,
        '--theme-spacing-medium': theme.value.spacing?.medium,
        '--theme-spacing-large': theme.value.spacing?.large,
        // Font
        ...(theme.value.fontFamily && { '--theme-font-family': theme.value.fontFamily })
    }));
    /**
     * Get inline style object for components
     */
    const getThemeStyles = () => themeCSSVariables.value;
    /**
     * Get button classes with theme colors
     */
    const getPrimaryButtonClasses = () => {
        return 'text-white py-3 px-6 rounded-lg font-semibold transition-colors duration-200';
    };
    /**
     * Get inline styles for primary button
     */
    const getPrimaryButtonStyles = () => ({
        backgroundColor: theme.value.colors.primary,
        borderRadius: theme.value.borderRadius
    });
    // Keep global CSS variables in sync so legacy utility classes
    // can still reflect project-specific branding.
    watchEffect(() => {
        if (typeof document === 'undefined')
            return;
        const root = document.documentElement;
        const vars = themeCSSVariables.value;
        Object.entries(vars).forEach(([key, value]) => {
            if (value != null) {
                root.style.setProperty(key, String(value));
            }
        });
    });
    return {
        theme,
        themeCSSVariables,
        getThemeStyles,
        getPrimaryButtonClasses,
        getPrimaryButtonStyles
    };
}
/**
 * Helper: Adjust color brightness
 * @param color - Hex color code (#RRGGBB)
 * @param percent - Percentage to adjust (-100 to 100)
 */
function adjustColorBrightness(color, percent) {
    // Remove # if present
    const hex = color.replace('#', '');
    // Parse RGB components
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // Adjust brightness
    const adjustedR = Math.max(0, Math.min(255, r + (r * percent / 100)));
    const adjustedG = Math.max(0, Math.min(255, g + (g * percent / 100)));
    const adjustedB = Math.max(0, Math.min(255, b + (b * percent / 100)));
    // Convert back to hex
    const toHex = (n) => {
        const hex = Math.round(n).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(adjustedR)}${toHex(adjustedG)}${toHex(adjustedB)}`;
}
